import {Injectable} from '@angular/core';
import {User} from "../auth/primitives/user";
import {Socket} from "socket.io-client";
import {DefaultEventsMap} from "@socket.io/component-emitter";
import {WebsocketService} from "./websocket.service";
import {Store} from "@ngrx/store";

import * as AuthSelectors from '../state/auth/auth.selector';
import * as UserSelectors from '../state/users/users.selector';
import * as ChatSelectors from '../state/chats/chat.selector';
import {Chat, Message, Participant} from "../state/chats/chat.model";
import {ChatActions} from "../state/chats/chatActions";
import {Router} from "@angular/router";
import {selectAuthIsReady} from "../state/auth/auth.selector";
import {IHasSocket} from "./i-has-socket";
import * as networkSelectors from "../state/network/network.selector";
import {selectWorldReady} from "../state/world/world.selector";
import {DoWhenReadyService, Readyables} from "./do-when-ready.service";

@Injectable({
    providedIn: 'root'
})
export class ChatsService implements IHasSocket {
    namespace = "chats"
    socket: Socket<DefaultEventsMap, DefaultEventsMap>
    user: User = null

    chatIds: string[] = []
    participants: Participant[] = []

    constructor(
        private websocketService: WebsocketService,
        private store: Store,
        private router: Router,
        private doWhenReady: DoWhenReadyService,
    ) {
        this.websocketService.chatsSocket.subscribe(async (socket) => {
            if (!socket) return
            this.socket = socket
            this.socket.connect()
            this.setupActionHandlers()
            this.store.select(AuthSelectors.selectAuthIsReady)
                .subscribe(async ({user, isGuest, authReady}) => {
                    if (!authReady) return;
                    this.user = user
                    if (user) {
                        await this.seedChatsAndMessages()
                    }
                })
        })
        this.store.select(ChatSelectors.selectAllChats).subscribe((chats) => {
            this.chatIds = chats.map(chat => chat.id)
        })
        this.store.select(ChatSelectors.selectAllParticipants).subscribe((participants) => {
            this.participants = participants
        })
    }

    setupActionHandlers() {
        this.socket.on("NewMessage", (jsonData) => {
            let message: Message = jsonData.message
            this.store.dispatch(ChatActions.foundMessage({
                message: message,
            }))
        });
        this.socket.on("NewChat", (jsonData) => {
            let chat: Chat = jsonData.chat
            let participants: Participant[] = jsonData.participants
            this.store.dispatch(ChatActions.foundChat({
                chat: chat,
            }))
            this.store.dispatch(ChatActions.foundParticipants({
                participants: participants,
            }))
        });
        this.socket.on("UpdateParticipant", (jsonData) => {
            let participant: Participant = jsonData.participant
            this.store.dispatch(ChatActions.foundParticipant({
                participant: participant,
            }))
        });
    }

    async seedChatsAndMessages() {
        const chatResponse = await this.socket
            .emitWithAck("get_users_chats", {
                userId: this.user.id,
            })
        const chats = chatResponse.chats as Chat[]
        const participants = chatResponse.participants as Participant[]
        const messages = chatResponse.messages as Message[]
        for (const chat of chats) {
            this.store.dispatch(ChatActions.foundChat({
                chat: chat,
            }))
        }
        this.store.dispatch(ChatActions.foundParticipants({
            participants: participants,
        }))
        this.store.dispatch(ChatActions.foundMultipleMessages({
            messages: messages,
        }))
    }

    async updateLastSeen(chatId: string, userId: string) {
        const updatedChat = await this.socket
            .emitWithAck("update_user_chat_last_seen", {
                chatId: chatId,
                userId: userId,
            })
        return JSON.parse(updatedChat) as Chat
    }

    async newChat(name: string, ownerId: string, participants: string[]) {
        const updatedChat = await this.socket
            .emitWithAck("new_chat", {
                name: name,
                ownerId: ownerId,
                participants: participants,
            })
        return JSON.parse(updatedChat) as Chat
    }

    async newMessage(chatId: string, text: string, ownerId: string, components: string[], componentProperties: any) {
        const updatedMessage = await this.socket
            .emitWithAck("new_message", {
                chatId: chatId,
                text: text,
                ownerId: ownerId,
                components: components,
                componentProperties: componentProperties,
            })
        return JSON.parse(updatedMessage) as Chat
    }

    async openChatWith(userId: string) {
        let participantsByChatId: { [key: string]: string[] } = {}
        for (const participant of this.participants) {
            participantsByChatId[participant.reference_id] ??= []
            participantsByChatId[participant.reference_id].push(participant.participant_id)
        }
        let chatId = Object.entries(participantsByChatId)
            .find(([_, ids]) => ids.length === 2 && ids.includes(userId))
            ?.[0] ?? null;
        if (chatId) {
            await this.router.navigate(
                [{outlets: {'left-pane': "chat"}}],
                {
                    queryParams: {
                        chatId: chatId,
                    },
                    queryParamsHandling: 'merge' // optional but common
                })
            return
        }
        await this.router.navigate(
            [{outlets: {'left-pane': "new-chat"}}],
            {
                queryParams: {
                    newChatUserId: userId,
                },
                queryParamsHandling: 'merge' // optional but common
            })
    }


}
